import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Badge from '@material-ui/core/Badge';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import moment from 'moment';
import MenuOutlined from '@material-ui/icons/MenuOutlined';
import ExpandMoreOutlined from '@material-ui/icons/ExpandMoreOutlined';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import Brightness7Outlined from '@material-ui/icons/Brightness7Outlined';
import Brightness4Outlined from '@material-ui/icons/Brightness4Outlined';
import NotificationsNoneOutlined from '@material-ui/icons/NotificationsNoneOutlined';
import AccountCircleOutlined from '@material-ui/icons/AccountCircleOutlined';
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';
import LockOutlined from '@material-ui/icons/LockOutlined';
import ExitToAppOutlined from '@material-ui/icons/ExitToAppOutlined';

import api from '../../services';

import { logoutRequest } from '../../actions';
import AceLogo from '../../components/AceLogo/AceLogo';
import NavIcon from '../../components/ui/NavIcon';
import { APP_HEADER_NAV_GROUPS, isGroupNavActive, pathMatchesNavLink } from '../../constants/appNav';
import { hasUploadedProfilePicture, parseStoredDisplayPicture } from '../../utils/profilePicture';

const THEME_STORAGE_KEY = 'appTheme';

const useStyles = makeStyles((theme) => ({
  bar: {
    position: 'fixed',
    top: 0,
    left: 'var(--app-shell-offset, 92px)',
    right: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minHeight: 60,
    padding: theme.spacing(0, 2),
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      left: 0,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  barDark: {
    background: 'rgba(15, 23, 42, 0.92)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    marginRight: theme.spacing(1),
    '& img': {
      height: 40,
      width: 'auto',
      maxWidth: 120,
      objectFit: 'contain',
    },
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobileNavBtn: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'inline-flex',
    },
  },
  navBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    letterSpacing: '0.02em',
    borderRadius: 10,
    padding: '6px 12px',
    color: theme.palette.text.primary,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    '&:hover': {
      background: 'rgba(var(--tenant-color-primary-rgb, 59, 130, 246), 0.1)',
      color: 'var(--tenant-color-primary, #3B82F6)',
    },
  },
  navBtnActive: {
    borderColor: 'rgba(var(--tenant-color-primary-rgb, 59, 130, 246), 0.35)',
    background: 'rgba(var(--tenant-color-primary-rgb, 59, 130, 246), 0.1)',
    color: 'var(--tenant-color-primary, #3B82F6)',
    '&:hover': {
      background: 'rgba(var(--tenant-color-primary-rgb, 59, 130, 246), 0.18)',
      color: 'var(--tenant-color-primary, #3B82F6)',
    },
  },
  navBtnDark: {
    color: '#e8eaf6',
    '&:hover': {
      background: 'rgba(255,255,255,0.1)',
      color: '#fff',
    },
  },
  navBtnActiveDark: {
    borderColor: 'rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    '&:hover': {
      background: 'rgba(255,255,255,0.2)',
      color: '#fff',
    },
  },
  searchWrap: {
    flex: '0 1 220px',
    maxWidth: 280,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 120px',
      maxWidth: 160,
    },
  },
  searchPaper: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    height: 36,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(0.5),
    border: '1px solid var(--border)',
    transition: 'border-color var(--transition), box-shadow var(--transition)',
    '&:focus-within': {
      borderColor: 'var(--brand)',
      boxShadow: '0 0 0 3px var(--brand-ring)',
    },
  },
  searchPaperDark: {
    borderColor: 'rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.06)',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  menuPaper: {
    borderRadius: 12,
    marginTop: 8,
    minWidth: 220,
    boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
  },
  notifMenuPaper: {
    borderRadius: 12,
    marginTop: 8,
    minWidth: 340,
    maxWidth: 380,
    boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
  },
  notifItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 16px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    whiteSpace: 'normal',
    cursor: 'pointer',
  },
  notifDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: 'var(--tenant-color-primary, #3B82F6)',
    marginTop: 5,
    flexShrink: 0,
  },
  notifDotRead: {
    background: 'transparent',
    border: '1px solid rgba(0,0,0,0.2)',
  },
  notifLink: {
    color: 'var(--tenant-color-primary, #3B82F6)',
    fontSize: 12,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  },
  menuLink: {
    fontSize: '0.875rem',
    borderRadius: 8,
    paddingTop: 10,
    paddingBottom: 10,
  },
  menuLinkActive: {
    backgroundColor: 'rgba(var(--tenant-color-primary-rgb, 59, 130, 246), 0.08)',
    color: 'var(--tenant-color-primary, #3B82F6)',
    fontWeight: 600,
  },
}));

function applyBodyTheme(isDark) {
  if (typeof document === 'undefined') return;
  if (isDark) {
    document.body.classList.add('dark-theme');
    localStorage.setItem(THEME_STORAGE_KEY, 'dark');
  } else {
    document.body.classList.remove('dark-theme');
    localStorage.setItem(THEME_STORAGE_KEY, 'light');
  }
}

function readStoredThemeIsDark() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
  } catch (_) {
    return false;
  }
}

const Header = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [dark, setDark] = useState(readStoredThemeIsDark);
  const [navAnchor, setNavAnchor] = useState(null);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [search, setSearch] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnreadCount(Number(data.count) || 0);
    } catch {
      /* silent — not signed in yet */
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 10, skip: 0 } });
      setNotifications(Array.isArray(data.items) ? data.items : []);
      setUnreadCount((data.items || []).filter((n) => !n.isRead).length);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  const handleNotifOpen = useCallback(
    async (e) => {
      setNotifAnchor(e.currentTarget);
      await fetchNotifications();
    },
    [fetchNotifications]
  );

  const handleMarkRead = useCallback(async (id) => {
    if (id === 'virtual-jobs-tomorrow') return;
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      /* silent */
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      /* silent */
    }
  }, []);

  const rawDp = localStorage.getItem('dp');
  const rawEmail = localStorage.getItem('email');
  const displayEmail = rawEmail ? String(rawEmail).replace(/^"|"$/g, '') : '';
  const showProfilePhoto = hasUploadedProfilePicture(rawDp);
  const profilePhotoSrc = showProfilePhoto ? parseStoredDisplayPicture(rawDp) : '';

  useEffect(() => {
    applyBodyTheme(dark);
  }, [dark]);

  const closeNavMenu = useCallback(() => {
    setNavAnchor(null);
    setOpenGroupId(null);
  }, []);

  const handleNavOpen = (event, groupId) => {
    setNavAnchor(event.currentTarget);
    setOpenGroupId(groupId);
  };

  const activeGroup = useMemo(
    () => APP_HEADER_NAV_GROUPS.find((g) => g.id === openGroupId),
    [openGroupId]
  );

  const logout = () => {
    localStorage.clear();
    dispatch(logoutRequest());
    window.location.href = '/';
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim();
    history.push(q ? `/operations/search?q=${encodeURIComponent(q)}` : '/operations/search');
  };

  useEffect(() => {
    if (location.pathname !== '/operations/search') return;
    const q = new URLSearchParams(location.search).get('q') || '';
    setSearch(q);
  }, [location.pathname, location.search]);

  const toggleDark = () => setDark((d) => !d);

  const isDark = dark;

  return (
    <header className={`app-top-header ${classes.bar} ${isDark ? classes.barDark : ''}`}>
      <div className={classes.left}>
        <Link to="/" className={classes.brand} aria-label="Home">
          <AceLogo width={40} />
        </Link>
        {isMdDown ? (
          <>
            <IconButton
              size="small"
              className={classes.mobileNavBtn}
              aria-label="Open menu"
              onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
            >
              <MenuOutlined fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={() => setMobileMenuAnchor(null)}
              classes={{ paper: classes.menuPaper }}
            >
              {APP_HEADER_NAV_GROUPS.flatMap((group) => [
                <MenuItem key={`h-${group.id}`} disabled dense style={{ opacity: 1 }}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700 }}>
                    {group.title}
                  </Typography>
                </MenuItem>,
                ...group.links.map((link) => (
                  <MenuItem
                    key={link.to}
                    dense
                    onClick={() => {
                      history.push(link.to);
                      setMobileMenuAnchor(null);
                    }}
                    className={
                      pathMatchesNavLink(location.pathname, link) ? classes.menuLinkActive : ''
                    }
                  >
                    <ListItemIcon style={{ minWidth: 36 }}>
                      <NavIcon icon={link.icon} fontSize="small" />
                    </ListItemIcon>
                    {link.title}
                  </MenuItem>
                )),
              ])}
            </Menu>
          </>
        ) : null}
        <nav className={classes.nav} aria-label="Main">
          {APP_HEADER_NAV_GROUPS.map((group) => {
            const active = isGroupNavActive(location.pathname, group);
            return (
              <button
                key={group.id}
                type="button"
                className={`${classes.navBtn} ${active ? classes.navBtnActive : ''} ${
                  isDark ? classes.navBtnDark : ''
                } ${active && isDark ? classes.navBtnActiveDark : ''}`}
                aria-haspopup="true"
                aria-expanded={openGroupId === group.id && Boolean(navAnchor)}
                onClick={(e) => handleNavOpen(e, group.id)}
              >
                {group.title}
                <ExpandMoreOutlined style={{ fontSize: 16, marginLeft: 2, opacity: 0.7 }} />
              </button>
            );
          })}
        </nav>
      </div>

      <Menu
        anchorEl={navAnchor}
        open={Boolean(navAnchor) && Boolean(activeGroup)}
        onClose={closeNavMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.menuPaper }}
        keepMounted
      >
        {activeGroup &&
          activeGroup.links.map((link) => (
            <MenuItem
              key={link.to}
              dense
              onClick={() => {
                history.push(link.to);
                closeNavMenu();
              }}
              className={`${classes.menuLink} ${
                pathMatchesNavLink(location.pathname, link) ? classes.menuLinkActive : ''
              }`}
            >
              <ListItemIcon style={{ minWidth: 36 }}>
                <NavIcon icon={link.icon} fontSize="small" />
              </ListItemIcon>
              {link.title}
            </MenuItem>
          ))}
      </Menu>

      <form
        className={classes.searchWrap}
        onSubmit={onSearchSubmit}
        role="search"
        style={{ flex: '0 1 220px', maxWidth: 280, minWidth: 0, width: '100%' }}
      >
        <Paper
          className={`${classes.searchPaper} ${isDark ? classes.searchPaperDark : ''}`}
          elevation={0}
        >
          <InputBase
            placeholder="People, team, invites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search operations' }}
            style={{ flex: 1, fontSize: 14 }}
          />
          <IconButton type="submit" size="small" aria-label="Search operations">
            <SearchOutlined fontSize="small" />
          </IconButton>
        </Paper>
      </form>

      <div className={classes.right}>
        <IconButton
          size="small"
          onClick={toggleDark}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          title={isDark ? 'Light mode' : 'Dark mode'}
        >
          {isDark ? (
            <Brightness7Outlined fontSize="small" style={{ color: 'var(--warning-strong)' }} />
          ) : (
            <Brightness4Outlined fontSize="small" />
          )}
        </IconButton>

        <IconButton
          size="small"
          aria-label="Notifications"
          aria-haspopup="true"
          aria-controls="header-notifications-menu"
          onClick={handleNotifOpen}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsNoneOutlined fontSize="small" />
          </Badge>
        </IconButton>
        <Menu
          id="header-notifications-menu"
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: classes.notifMenuPaper }}
          getContentAnchorEl={null}
        >
          <Box px={2} pt={1.5} pb={1} display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {unreadCount} unread
                </Typography>
              )}
            </Box>
            {unreadCount > 0 && (
              <Typography
                variant="caption"
                color="primary"
                style={{ cursor: 'pointer', fontWeight: 500 }}
                onClick={handleMarkAllRead}
              >
                Mark all read
              </Typography>
            )}
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <Box px={2} py={2}>
              <Typography variant="body2" color="textSecondary">
                No new notifications.
              </Typography>
            </Box>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                className={classes.notifItem}
                style={{ background: n.isRead ? 'transparent' : 'rgba(59,130,246,0.04)' }}
                onClick={() => handleMarkRead(n.id)}
              >
                <span className={`${classes.notifDot} ${n.isRead ? classes.notifDotRead : ''}`} />
                <Box flex={1}>
                  <Typography variant="body2" style={{ fontWeight: n.isRead ? 400 : 600, lineHeight: 1.4 }}>
                    {n.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 2 }}>
                    {n.message}
                  </Typography>
                  <Box mt={0.5} display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Typography variant="caption" color="textSecondary">
                      {n.createdAt ? moment(n.createdAt).fromNow() : ''}
                    </Typography>
                    {n.link && (
                      <Link
                        to={n.link}
                        className={classes.notifLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotifAnchor(null);
                        }}
                      >
                        View →
                      </Link>
                    )}
                  </Box>
                </Box>
              </div>
            ))
          )}
          <Divider />
          <Box px={2} py={1}>
            <Link
              to="/settings-profile?tab=notifications"
              className={classes.notifLink}
              onClick={() => setNotifAnchor(null)}
            >
              See all notifications →
            </Link>
          </Box>
        </Menu>

        <IconButton
          size="small"
          aria-label="Account menu"
          aria-haspopup="true"
          onClick={(e) => setProfileAnchor(e.currentTarget)}
        >
          <AccountCircleOutlined fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={() => setProfileAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: classes.menuPaper }}
        >
          <Box
            px={2}
            py={1.5}
            display="flex"
            alignItems="center"
            style={{ gap: 12 }}
          >
            {showProfilePhoto ? (
              <span className="thumb">
                <img
                  src={profilePhotoSrc}
                  alt=""
                  height={40}
                  width={40}
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              </span>
            ) : null}
            <Typography variant="body2" noWrap style={{ maxWidth: showProfilePhoto ? 200 : 260 }}>
              {displayEmail || 'Signed in'}
            </Typography>
          </Box>
          <Divider />
          <MenuItem component={Link} to="/settings-profile?tab=profile" onClick={() => setProfileAnchor(null)}>
            <ListItemIcon style={{ minWidth: 36 }}>
              <SettingsOutlined fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem component={Link} to="/account-locked" onClick={() => setProfileAnchor(null)}>
            <ListItemIcon style={{ minWidth: 36 }}>
              <LockOutlined fontSize="small" />
            </ListItemIcon>
            Lock
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setProfileAnchor(null);
              logout();
            }}
            style={{ color: 'var(--danger-strong)' }}
          >
            <ListItemIcon style={{ minWidth: 36 }}>
              <ExitToAppOutlined fontSize="small" style={{ color: 'var(--danger-strong)' }} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
};

export default Header;
